import React, { useRef } from 'react';
import { EFFECT_SPECS } from './effectSpecs';
import {
    DEFAULT_CLIP_VOLUME,
    MAX_CLIP_VOLUME,
    clipVolume,
    formatTimeLabel,
    insetSoundFrom,
    isImageAssetType,
    pipParams,
} from '../state/timelineModel';

/**
 * The properties of whatever is selected.
 *
 * One panel for every kind of selection, so "change this thing" is always the
 * same gesture in the same place. Before this, each effect had its own modal
 * reached from a dropdown that had no connection to what was selected on the
 * timeline — you picked the clip twice, in two different widgets, and the
 * timeline never showed the result.
 */

function Field({ field, value, onChange }) {
    const current = value ?? '';

    if (field.type === 'range') {
        const numeric = Number(current) || 0;
        return (
            <label className="nle-field">
                <span className="nle-field-label">
                    {field.label}
                    <em>{field.format ? field.format(numeric) : numeric}</em>
                </span>
                <input
                    type="range"
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={numeric}
                    onChange={(e) => onChange(Number(e.target.value))}
                />
            </label>
        );
    }

    if (field.type === 'toggle') {
        return (
            <label className="nle-field nle-field-inline">
                <input
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={(e) => onChange(e.target.checked)}
                />
                <span className="nle-field-label">{field.label}</span>
            </label>
        );
    }

    if (field.type === 'select') {
        return (
            <label className="nle-field">
                <span className="nle-field-label">{field.label}</span>
                <select value={current} onChange={(e) => onChange(e.target.value)}>
                    {field.options.map(option => (
                        <option key={option} value={option}>{option || 'Default'}</option>
                    ))}
                </select>
            </label>
        );
    }

    if (field.type === 'color') {
        return (
            <label className="nle-field nle-field-inline">
                <input
                    type="color"
                    value={current || '#ffffff'}
                    onChange={(e) => onChange(e.target.value)}
                />
                <span className="nle-field-label">{field.label}</span>
            </label>
        );
    }

    if (field.type === 'asset') {
        return (
            <label className="nle-field">
                <span className="nle-field-label">{field.label}</span>
                <select value={current} onChange={(e) => onChange(e.target.value)}>
                    <option value="">Choose…</option>
                    {(field.assets || []).map(asset => (
                        <option key={asset.FileName} value={asset.FileName}>
                            {asset.FileAlias || asset.FileName}
                        </option>
                    ))}
                </select>
            </label>
        );
    }

    return (
        <label className="nle-field">
            <span className="nle-field-label">{field.label}</span>
            <input type="text" value={current} onChange={(e) => onChange(e.target.value)} />
        </label>
    );
}

const formatVolume = (level) => (level === 0 ? 'Muted' : `${Math.round(level * 100)}%`);

/**
 * How loud this clip plays, and the one-click way to silence it.
 *
 * Mute is not a second field: it is this level at zero, matching how the render
 * side states it. What the button adds over dragging the slider to the far left
 * is the way back — the level it was at before the mute is remembered here, so
 * unmuting restores it rather than guessing 100%.
 */
function VolumeControl({ clip, editable, onChange }) {
    const level = clipVolume(clip);
    // Remembered per clip: the panel is reused as the selection moves, so an
    // unremembered id must start at the default rather than inherit the level
    // of whichever clip was selected before.
    const lastAudible = useRef({ id: clip.id, level: DEFAULT_CLIP_VOLUME });
    if (lastAudible.current.id !== clip.id) {
        lastAudible.current = { id: clip.id, level: DEFAULT_CLIP_VOLUME };
    }
    if (level > 0) lastAudible.current.level = level;

    return (
        <div className="nle-clip-audio-controls">
            <label className="nle-field">
                <span className="nle-field-label">
                    Volume
                    <em>{formatVolume(level)}</em>
                </span>
                <input
                    type="range"
                    min={0}
                    max={MAX_CLIP_VOLUME}
                    step={0.05}
                    value={level}
                    disabled={!editable}
                    // Coalesced: one drag is one undo step, not sixty.
                    onChange={(e) => onChange({ volume: Number(e.target.value) }, true)}
                />
            </label>
            {editable && (
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => onChange({ volume: level > 0 ? 0 : lastAudible.current.level })}
                >
                    {level > 0 ? 'Mute this clip' : 'Unmute'}
                </button>
            )}
        </div>
    );
}

/**
 * Where an inset sits and how big it is. Position is set by dragging it on the
 * preview -- a pair of number fields would be a worse way to say "there" -- so
 * this panel carries the one thing dragging cannot: its size.
 */
function InsetControls({ clip, mainEnd, editable, onChange }) {
    const params = pipParams(clip);
    const cutShort = clip.end > mainEnd;

    return (
        <div className="nle-clip-audio-controls">
            <label className="nle-field">
                <span className="nle-field-label">
                    Size
                    <em>{Math.round(params.scale * 100)}%</em>
                </span>
                <input
                    type="range"
                    min={0.1}
                    max={1}
                    step={0.01}
                    value={params.scale}
                    disabled={!editable}
                    onChange={(e) => onChange({ params: { ...params, scale: Number(e.target.value) } }, true)}
                />
            </label>
            {cutShort && (
                <p className="nle-inspector-hint">
                    {clip.start >= mainEnd
                        ? 'This starts after the main video ends, so it will not appear in the render.'
                        : `The main video ends at ${formatTimeLabel(mainEnd)}, so this is cut there in the render.`}
                </p>
            )}
        </div>
    );
}

const SOUND_CHOICES = [
    { value: 'main', label: 'Main video' },
    { value: 'inset', label: 'This overlay' },
    { value: 'both', label: 'Both' },
];

/**
 * Whose sound plays while the inset is on screen -- one choice rather than a
 * mute button on each clip, because "the overlay alone" is a statement about
 * both of them at once. The level applies only when the overlay is heard, and
 * stops short of zero: an overlay at zero is simply "Main video".
 */
function InsetSound({ clip, editable, onChange }) {
    const soundFrom = insetSoundFrom(clip);
    // An inset from before the choice existed may be at 0; picking a source
    // that hears it must also make it audible.
    const level = Math.max(0.05, clipVolume(clip) || DEFAULT_CLIP_VOLUME);

    return (
        <div className="nle-clip-audio-controls">
            <span className="nle-field-label">Sound while it plays</span>
            <div className="nle-segmented" role="radiogroup" aria-label="Sound while it plays">
                {SOUND_CHOICES.map(choice => (
                    <button
                        key={choice.value}
                        type="button"
                        role="radio"
                        aria-checked={soundFrom === choice.value}
                        className={`btn btn-secondary btn-sm ${soundFrom === choice.value ? 'active' : ''}`}
                        disabled={!editable}
                        onClick={() => onChange({ soundFrom: choice.value, volume: level })}
                    >
                        {choice.label}
                    </button>
                ))}
            </div>
            {soundFrom !== 'main' && (
                <label className="nle-field">
                    <span className="nle-field-label">
                        Overlay level
                        <em>{formatVolume(level)}</em>
                    </span>
                    <input
                        type="range"
                        min={0.05}
                        max={MAX_CLIP_VOLUME}
                        step={0.05}
                        value={level}
                        disabled={!editable}
                        onChange={(e) => onChange({ volume: Number(e.target.value) }, true)}
                    />
                </label>
            )}
        </div>
    );
}

function ClipPanel({ clip, insetInfo, assetsByFileName, editable, onChange }) {
    const asset = assetsByFileName.get(String(clip.asset_filename || ''));
    // A still has no soundtrack to set the level of, and asking the renderer to
    // set one is an error rather than a no-op — so the control is not offered.
    const hasSound = !isImageAssetType(asset?.FileType);

    if (insetInfo) {
        return (
            <>
                <h4 className="nle-inspector-title">{asset?.FileAlias || clip.asset_filename || 'Overlay'}</h4>
                <dl className="nle-facts">
                    <dt>Over the video</dt>
                    <dd>{formatTimeLabel(clip.start)} → {formatTimeLabel(clip.end)}</dd>
                    <dt>From the source</dt>
                    <dd>{formatTimeLabel(clip.sourceIn)} → {formatTimeLabel(clip.sourceOut)}</dd>
                </dl>
                <InsetControls clip={clip} mainEnd={insetInfo.mainEnd} editable={editable} onChange={onChange} />
                {hasSound && <InsetSound clip={clip} editable={editable} onChange={onChange} />}
                <p className="nle-inspector-hint">
                    Drag it on the preview to move it. It plays over whatever is on
                    the video track.
                </p>
            </>
        );
    }

    return (
        <>
            <h4 className="nle-inspector-title">{asset?.FileAlias || clip.asset_filename || 'Clip'}</h4>
            <dl className="nle-facts">
                <dt>On the track</dt>
                <dd>{formatTimeLabel(clip.start)} → {formatTimeLabel(clip.end)}</dd>
                <dt>From the source</dt>
                <dd>{formatTimeLabel(clip.sourceIn)} → {formatTimeLabel(clip.sourceOut)}</dd>
                <dt>Length</dt>
                <dd>{formatTimeLabel(clip.duration)}</dd>
            </dl>
            {hasSound && <VolumeControl clip={clip} editable={editable} onChange={onChange} />}
            <p className="nle-inspector-hint">
                Drag the clip to move it, or its edges to change which part of the
                source it plays. Press S to split it at the playhead.
            </p>
        </>
    );
}

function EffectPanel({ effect, assets, editable, onChange, onDelete }) {
    if (effect.readOnly) {
        return (
            <>
                <h4 className="nle-inspector-title">{effect.label || effect.tool_name}</h4>
                <p className="nle-inspector-hint">
                    This step came from an AI plan and has no editor here yet. It stays
                    in the edit and renders as planned — it just cannot be adjusted from
                    this panel.
                </p>
                {editable && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={onDelete}>
                        Remove from the edit
                    </button>
                )}
            </>
        );
    }

    const spec = EFFECT_SPECS[effect.type];
    if (!spec) return <p className="nle-inspector-hint">Unknown effect.</p>;

    const params = effect.params || {};
    const imageAssets = assets.filter(asset => isImageAssetType(asset.FileType));

    return (
        <>
            <h4 className="nle-inspector-title">{spec.label}</h4>

            {spec.fields.map(field => (
                <Field
                    key={field.key}
                    field={field.type === 'asset' ? { ...field, assets: imageAssets } : field}
                    value={params[field.key] ?? spec.defaults[field.key]}
                    onChange={(next) => onChange({ params: { ...params, [field.key]: next } })}
                />
            ))}

            {spec.hint && <p className="nle-inspector-hint">{spec.hint}</p>}

            <div className="nle-field-row">
                <label className="nle-field">
                    <span className="nle-field-label">Starts</span>
                    <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={effect.start ?? 0}
                        onChange={(e) => onChange({ start: Number(e.target.value) })}
                    />
                </label>
                <label className="nle-field">
                    <span className="nle-field-label">Ends</span>
                    <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={effect.end ?? 0}
                        onChange={(e) => onChange({ end: Number(e.target.value) })}
                    />
                </label>
            </div>

            {editable && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={onDelete}>
                    Remove effect
                </button>
            )}
        </>
    );
}

function Inspector({
    selectedItem, selection, insetInfo, assets, assetsByFileName, editable, onChange, onDelete,
}) {
    return (
        <aside className="nle-inspector">
            {!selectedItem ? (
                <p className="nle-inspector-empty">
                    Select a clip or an effect on the timeline to see its properties.
                </p>
            ) : selection?.kind === 'effect' ? (
                <EffectPanel
                    effect={selectedItem}
                    assets={assets}
                    editable={editable}
                    onChange={onChange}
                    onDelete={onDelete}
                />
            ) : (
                <ClipPanel
                    clip={selectedItem}
                    insetInfo={insetInfo}
                    assetsByFileName={assetsByFileName}
                    editable={editable}
                    onChange={onChange}
                />
            )}
        </aside>
    );
}

export default Inspector;
